import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Utils } from '@utils/Utils';
import { buildUrl } from '@utils/url/url.util';
import { RESTServerRoute } from '../../types/Server';
import { ChargingProfileDataResult, SmartChargingCostComponentDataResult } from '../../types/DataResult';
import {
  CostCurveDTO,
  SmartChargingTariffPlanCreateOrUpdate,
  SmartChargingTariffPlanView,
} from '../../types/SmartChargingTariffPlan';
import { BaseRequestParams } from '../../types/RequestParams';
import { CostComponentCreateDTO, CostComponentViewDTO } from '../../types/SmartChargingCostComponent';

@Injectable()
export class SmartChargingApiService {
  public constructor(public httpClient: HttpClient) {}

  public getChargingProfiles({
    chargingStationId,
  }: {
    chargingStationId: string;
  }): Observable<ChargingProfileDataResult> {
    const params = { ChargingStationID: chargingStationId };

    return this.httpClient.get<ChargingProfileDataResult>(RESTServerRoute.REST_CHARGING_PROFILES, { params });
  }

  public getSmartChargingTariffPlan(id: string): Observable<SmartChargingTariffPlanView> {
    const url = buildUrl(RESTServerRoute.REST_SMART_CHARGING_TARIFF_PLAN, { id });
    return this.httpClient.get<SmartChargingTariffPlanView>(url);
  }

  public getSmartChargingTariffPlanCurrency(): Observable<string> {
    return this.httpClient.get<string>(RESTServerRoute.REST_SMART_CHARGING_TARIFF_PLANS_CURRENCY);
  }

  public setSmartChargingTariffPlanCurrency(currency: string): Observable<string> {
    return this.httpClient
      .post<void>(RESTServerRoute.REST_SMART_CHARGING_TARIFF_PLANS_CURRENCY, { currency })
      .pipe(map(() => currency));
  }

  public createSmartChargingTariffPlan(
    tariffPlan: SmartChargingTariffPlanCreateOrUpdate,
  ): Observable<SmartChargingTariffPlanView> {
    return this.httpClient.post<SmartChargingTariffPlanView>(
      RESTServerRoute.REST_SMART_CHARGING_TARIFF_PLANS,
      tariffPlan,
    );
  }

  public updateSmartChargingTariffPlan(
    tariffPlanId: string,
    tariffPlan: SmartChargingTariffPlanCreateOrUpdate,
  ): Observable<SmartChargingTariffPlanView> {
    const url = buildUrl(RESTServerRoute.REST_SMART_CHARGING_TARIFF_PLAN, { id: tariffPlanId });
    return this.httpClient.patch<SmartChargingTariffPlanView>(url, tariffPlan);
  }

  public getCostComponents(params: BaseRequestParams): Observable<SmartChargingCostComponentDataResult> {
    return this.httpClient
      .get<SmartChargingCostComponentDataResult>(RESTServerRoute.REST_SMART_CHARGING_COST_COMPONENTS, {
        params: Utils.cloneObjectAsHttpParams(params),
      })
      .pipe(
        map((response) => {
          if (Array.isArray(response)) {
            return {
              count: response.length,
              result: response,
            } satisfies SmartChargingCostComponentDataResult;
          }
          return response;
        }),
      );
  }

  public getCostComponent(id: string, from?: Date, to?: Date): Observable<CostComponentViewDTO> {
    const url = buildUrl(RESTServerRoute.REST_SMART_CHARGING_COST_COMPONENT, { id });
    const params: Record<string, string> = {};
    if (from) params['from'] = from.toISOString();
    if (to) params['to'] = to.toISOString();
    return this.httpClient.get<CostComponentViewDTO>(url, { params });
  }

  public createCostComponent(costComponent: CostComponentCreateDTO): Observable<CostComponentViewDTO> {
    return this.httpClient.post<CostComponentViewDTO>(
      RESTServerRoute.REST_SMART_CHARGING_COST_COMPONENTS,
      costComponent,
    );
  }

  public getAvailableDayAheadCurves(): Observable<string[]> {
    return this.httpClient.get<string[]>(RESTServerRoute.REST_SMART_CHARGING_COST_COMPONENT_GET_CURVE);
  }

  public archiveCostComponent(ids: string[]): Observable<void> {
    return this.httpClient.patch<void>(RESTServerRoute.REST_SMART_CHARGING_COST_COMPONENTS_ARCHIVE, { ids });
  }

  public getTariffPlanCurve(SiteAreaID: string, from?: Date, to?: Date): Observable<CostCurveDTO[]> {
    const params: Record<string, string> = { SiteAreaID: SiteAreaID };
    if (from) params['from'] = from.toISOString();
    if (to) params['to'] = to.toISOString();
    return this.httpClient.get<CostCurveDTO[]>(RESTServerRoute.REST_SMART_CHARGING_TARIFF_PLAN_CURVE, { params });
  }
}
